import React, { useMemo, useState } from 'react';
import Popover from './Popover';
import Input from './Input';
import Checkbox from './Checkbox';
import { Icon } from '../icons';
import { useT } from '../../i18n';

/**
 * MultiSelect — searchable multi-select with chips shown in the trigger.
 *
 * Props:
 *   options: Array<{ value, label }>
 *   value: value[]                 — selected values
 *   onChange(value[])
 *   placeholder, searchPlaceholder
 *   maxTags: number                — chips before "+N more" (default 3)
 *   size: 'sm' | 'md'
 *   disabled, ariaLabel
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder,
  searchPlaceholder,
  maxTags = 3,
  size = 'md',
  disabled = false,
  ariaLabel,
  matchTriggerWidth = true,
  className = '',
}) => {
  const t = useT();
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(() => new Set(value.map(String)), [value]);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = (val) => {
    const has = selectedSet.has(String(val));
    onChange?.(has ? value.filter((v) => String(v) !== String(val)) : [...value, val]);
  };
  const remove = (val, e) => { e.stopPropagation(); toggle(val); };

  const selectedOpts = options.filter((o) => selectedSet.has(String(o.value)));
  const shown = selectedOpts.slice(0, maxTags);
  const extra = selectedOpts.length - shown.length;

  const trigger = (
    <button
      type="button"
      className={`ps-select ps-multiselect ps-select--${size} ${disabled ? 'is-disabled' : ''}`.trim()}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <span className="ps-multiselect__tags">
        {selectedOpts.length === 0 && <span className="ps-select__value--placeholder">{placeholder ?? t('Select…')}</span>}
        {shown.map((o) => (
          <span key={o.value} className="ps-multiselect__tag">
            {o.label}
            <span
              role="button"
              tabIndex={-1}
              aria-label={t('Remove {label}', { label: o.label })}
              className="ps-multiselect__tagx"
              onClick={(e) => remove(o.value, e)}
            >
              <Icon.X size={11} />
            </span>
          </span>
        ))}
        {extra > 0 && <span className="ps-multiselect__more">+{extra}</span>}
      </span>
      <Icon.ChevronsUpDown size={16} className="ps-select__caret" />
    </button>
  );

  return (
    <Popover
      trigger={trigger}
      role="dialog"
      placement="bottom-start"
      matchTriggerWidth={matchTriggerWidth}
      className={className}
      panelClassName="ps-popover__panel--combobox"
    >
      {() => (
        <div className="ps-combobox">
          <div className="ps-combobox__search">
            <Input
              size="sm"
              autoFocus
              leadingIcon={<Icon.Search />}
              placeholder={searchPlaceholder ?? t('Search…')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ps-combobox__list">
            {filtered.length === 0 ? (
              <p className="ps-combobox__empty">{t('No matches')}</p>
            ) : (
              filtered.map((o) => (
                <Checkbox
                  key={o.value}
                  className="ps-fedit__check"
                  label={o.label}
                  checked={selectedSet.has(String(o.value))}
                  onChange={() => toggle(o.value)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </Popover>
  );
};

export default MultiSelect;
